/**
 * DocumentViewer Component
 * Displays documents inline (PDFs) or opens them in new tabs for other formats
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import { documentsService, Document } from '../../services/documentsService';

interface DocumentViewerProps {
  document: Document | null;
  open: boolean;
  onClose: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document: doc,
  open,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPDF = (filename: string) => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  const getFileIcon = (filename: string) => {
    if (isPDF(filename)) {
      return <PdfIcon color="error" />;
    }
    return <DocumentIcon color="action" />;
  };

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toUpperCase() || 'FILE';
  };

  const handleDownload = async () => {
    if (!doc) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const blob = await documentsService.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file?.split('/').pop() || `${doc.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (!doc?.file) return;
    
    // Use the file URL directly from the API - it's already complete
    window.open(doc.file, '_blank');
  };

  const renderDocumentPreview = () => {
    if (!doc?.file) {
      return (
        <Alert severity="warning">
          No file available for this document.
        </Alert>
      );
    }

    const filename = doc.file.split('/').pop() || '';
    
    // Use the file URL directly from the API - it's already complete
    const fileUrl = doc.file;

    if (isPDF(filename)) {
      return (
        <Box sx={{ width: '100%', height: '600px', border: '1px solid #ddd' }}>
          <iframe
            src={`${fileUrl}#toolbar=1&navpanes=1&scrollbar=1`}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title={doc.title}
          />
        </Box>
      );
    }

    // For non-PDF files, show a preview card
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          p: 4,
          border: '2px dashed #ddd',
          borderRadius: 2,
          bgcolor: 'grey.50'
        }}
      >
        {getFileIcon(filename)}
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          {doc.title}
        </Typography>
        <Chip 
          label={getFileExtension(filename)} 
          size="small" 
          color="primary" 
          sx={{ mb: 2 }}
        />
        <Typography variant="body2" color="text.secondary" align="center">
          This file type cannot be previewed inline.
          Click "Open in New Tab" to view or "Download" to save locally.
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
          >
            Open in New Tab
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Download'}
          </Button>
        </Box>
      </Box>
    );
  };

  if (!doc) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{doc.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {doc.document_type_name} • Version {doc.version} • {doc.status}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {renderDocumentPreview()}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : 'Download'}
        </Button>
        <Button
          startIcon={<OpenInNewIcon />}
          onClick={handleOpenInNewTab}
        >
          Open in New Tab
        </Button>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer; 